import {
  isAdvertisingAllowed,
  isAdvertisingExplicitlyDenied,
} from "../Helpers/cookieConsent";

const scriptExists = (src: string) =>
  typeof window !== "undefined" && document.querySelector(`[src="${src}"]`);

// Initialize BIFF command queue early
const initBiffCommandQueue = () => {
  if (typeof window === "undefined") return;

  window.brg = window.brg || {};
  window.brg.ui = window.brg.ui || {};
  window.brg.ui.biff = window.brg.ui.biff || {
    withAuctions: () => ({ load: () => Promise.resolve() }),
    load: () => Promise.resolve(),
    cmd: [],
  };
  window.brg.ui.biff.cmd = window.brg.ui.biff.cmd || [];
};

// Initialize BIFF once globally
export const initBiff = (
  isProduction: boolean,
  isLoggedIn: boolean
): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("Window not available"));
      return;
    }

    const biffSrc = "https://cdn.biff.travel/scripts/7/biff.min.js";

    // Initialize command queue first
    initBiffCommandQueue();

    // Skip if already loaded
    if (scriptExists(biffSrc)) {
      resolve();
      return;
    }

    // Set API endpoint based on environment (v3 as per official BIFF docs)
    const apiEndpoint = isProduction
      ? "https://cdn.biff.travel/api/v3/"
      : "https://qa-cdn.biff.travel/api/v3/";

    // Set envConfig for BIFF (as per official docs)
    window.envConfig = {
      ...window.envConfig,
      biffUrl: apiEndpoint,
    };

    // Load biff script
    const biffScript = document.createElement("script");
    biffScript.src = biffSrc;
    biffScript.async = true;
    document.head.appendChild(biffScript);

    biffScript.onload = () => {
      // Load GPT script first
      const gptSrc = "https://securepubads.g.doubleclick.net/tag/js/gpt.js";
      if (!scriptExists(gptSrc)) {
        const gptScript = document.createElement("script");
        gptScript.src = gptSrc;
        gptScript.async = true;
        document.head.appendChild(gptScript);

        gptScript.onload = () => {
          initializeBiffWithGPT();
        };

        gptScript.onerror = () => {
          console.error("Failed to load GPT script");
          reject(new Error("Failed to load GPT script"));
        };
      } else {
        // GPT script already loaded
        initializeBiffWithGPT();
      }

      function initializeBiffWithGPT() {
        // Use BIFF command queue
        window?.brg?.ui?.biff?.cmd?.push((biff) => {
          // TODO ask biff team to accept ICARUS as platformName
          // WORKAROUND: Temporarily change platformName to SUNRISE for BIFF
          // while preserving original ICARUS value for tracking
          let originalPlatformName: string | undefined;

          if (window.platformData) {
            // Find the visit event in platformData array
            const visitEvent = window.platformData.find(
              (item: any) => item.event === "visit"
            );

            if (visitEvent?.page?.platformName === "ICARUS") {
              originalPlatformName = visitEvent.page.platformName;
              visitEvent.page.platformName = "SUNRISE";
            }
          }

          // Enable auctions and load BIFF (as per docs)
          biff.withAuctions();
          biff
            .load({ isLoggedIn })
            .then(() => {
              // TODO ask biff team to accept ICARUS as platformName
              // WORKAROUND: Restore original platformName after BIFF loads
              if (originalPlatformName && window.platformData) {
                const visitEvent = window.platformData.find(
                  (item: any) => item.event === "visit"
                );
                if (visitEvent?.page) {
                  visitEvent.page.platformName = originalPlatformName;
                }
              }

              resolve();
            })
            .catch((error: unknown) => {
              // TODO ask biff team to accept ICARUS as platformName
              // WORKAROUND: Restore original platformName even on error
              if (originalPlatformName && window.platformData) {
                const visitEvent = window.platformData.find(
                  (item: any) => item.event === "visit"
                );
                if (visitEvent?.page) {
                  visitEvent.page.platformName = originalPlatformName;
                }
              }

              console.error("Failed to load BIFF:", error);
              reject(error instanceof Error ? error : new Error(String(error)));
            });
        });
      }
    };

    biffScript.onerror = () => {
      const error = new Error("Failed to load biff script");
      console.error(error);
      reject(error);
    };
  });
};

// Check for cookie consent and visit event before enabling ads
// Maximum time to wait for consent (in seconds) to prevent infinite polling
const MAX_CONSENT_WAIT_SECONDS = 60;

export const waitForCookieConsent = (): Promise<{
  shouldInitializeBiff: boolean;
}> => {
  return new Promise((resolve) => {
    if (typeof window === "undefined") {
      resolve({ shouldInitializeBiff: false });
      return;
    }

    let retryCount = 0;

    const checkConsent = () => {
      // Stop polling after max retries to prevent memory leak from infinite loops
      if (retryCount >= MAX_CONSENT_WAIT_SECONDS) {
        resolve({ shouldInitializeBiff: false });
        return;
      }
      retryCount++;

      // Check if advertising has been explicitly denied
      if (isAdvertisingExplicitlyDenied()) {
        resolve({ shouldInitializeBiff: false });
        return;
      }

      // Check for both advertising consent and visit event
      const hasConsent = isAdvertisingAllowed();
      const hasVisitEvent = window.platformData?.some(
        (event: any) => event.event === "visit"
      );

      if (hasConsent && hasVisitEvent) {
        // Extract visit event data and add to platformData for BIFF
        const visitEvent = window.platformData?.find(
          (event: any) => event.event === "visit"
        );

        if (visitEvent) {
          // Add visit event properties directly to platformData object for BIFF
          Object.assign(window.platformData, {
            transactionAffiliation: visitEvent.transactionAffiliation,
            page: visitEvent.page,
            search: visitEvent.search,
            user: visitEvent.user,
            product: visitEvent.product,
          });
        }

        resolve({ shouldInitializeBiff: true });
        return;
      }
      // poll every second
      setTimeout(checkConsent, 1000);
    };

    // Start checking
    checkConsent();
  });
};
